import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { posGet, posPost, avisaError, dinero, hoy, sumaDias, fechaHora, hora, METODOS } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { ticketCorte } from "./posTickets";
import SinPermiso from "./SinPermiso";
import { AuthContext } from "../../contexts/AuthContext";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const METODOS_K = ["efectivo", "tdc", "transfer"];

function PosCorte() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(false);
  const { tienda } = ctx;
  const [pendiente, setPendiente] = useState(null);
  const [entregado, setEntregado] = useState({ efectivo: "", tdc: "", transfer: "" });
  const [observaciones, setObservaciones] = useState("");
  const [fondoQueda, setFondoQueda] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -7));
  const [fin, setFin] = useState(hoy());

  const cargar = useCallback(() => {
    if (!tienda) return;
    posGet("/cortes/pendiente", { area: tienda })
      .then((p) => {
        setPendiente(p);
        // Tarjeta y transferencia se proponen igual al sistema; el efectivo se cuenta
        setEntregado({ efectivo: "", tdc: String(p.sistema.tdc), transfer: String(p.sistema.transfer) });
        // Propone el mismo fondo que dejó la tienda la última vez
        let f = "";
        try { f = localStorage.getItem(`posFondo_${tienda}`) || ""; } catch (e) { f = ""; }
        setFondoQueda(f);
      })
      .catch(avisaError);
    posGet("/cortes", { area: tienda, inicio, fin }).then(setHistorial).catch(avisaError);
  }, [tienda, inicio, fin]);

  useEffect(() => { cargar(); }, [cargar]);

  const total = r2(METODOS_K.reduce((t, k) => t + (Number(entregado[k]) || 0), 0));
  const dif = (k) => r2((Number(entregado[k]) || 0) - (pendiente ? pendiente.sistema[k] : 0));

  const fondo = r2(fondoQueda);
  const aEntregar = r2((Number(entregado.efectivo) || 0) - fondo);

  const hacerCorte = async () => {
    if (entregado.efectivo === "") return Swal.fire({ icon: "warning", title: "Cuenta el efectivo", text: "Captura cuánto efectivo hay en caja (puede ser 0)." });
    if (METODOS_K.some((k) => Number(entregado[k]) < 0) || fondo < 0) return Swal.fire({ icon: "warning", title: "Los importes no pueden ser negativos" });
    if (aEntregar < 0) return Swal.fire({ icon: "warning", title: "El fondo no puede ser mayor al efectivo contado" });
    const difs = METODOS_K.filter((k) => dif(k) !== 0);
    if (difs.length && !observaciones.trim()) return Swal.fire({ icon: "warning", title: "Hay diferencias", text: "Explica el motivo en observaciones antes de cerrar el corte." });
    const r = await Swal.fire({
      icon: difs.length ? "warning" : "question",
      title: "¿Cerrar el corte?",
      html: (difs.length
        ? difs.map((k) => `${METODOS[k]}: <strong>${dinero(dif(k))}</strong> (${dif(k) > 0 ? "sobrante" : "faltante"})`).join("<br/>")
        : "Todo cuadra.") +
        `<br/><br/>Efectivo a entregar: <strong>${dinero(aEntregar)}</strong>` + (fondo > 0 ? `<br/>Se queda de fondo en caja: <strong>${dinero(fondo)}</strong>` : ""),
      showCancelButton: true, confirmButtonText: "Sí, cerrar corte", cancelButtonText: "Revisar",
    });
    if (!r.value) return;
    setGuardando(true);
    try {
      const c = await posPost("/cortes", { area: tienda, entregado, observaciones, fondoQueda: fondo });
      try { localStorage.setItem(`posFondo_${tienda}`, fondo ? String(fondo) : ""); } catch (e) { /* sin almacenamiento */ }
      const completo = await posGet(`/cortes/${c._id}`, { area: tienda });
      ticketCorte(completo);
      Swal.fire({ icon: "success", title: `Corte ${c.folio} cerrado`, timer: 1800, showConfirmButton: false });
      setObservaciones("");
      cargar();
    } catch (e) {
      avisaError(e);
    } finally {
      setGuardando(false);
    }
  };

  const reimprimir = async (id) => {
    try { ticketCorte(await posGet(`/cortes/${id}`, { area: tienda })); } catch (e) { avisaError(e); }
  };

  if (!(user && user.rolTiendas && user.posCorte)) return <SinPermiso que="hacer cortes de caja" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-money-check-alt mr-2" style={{ color: "#f59e0b" }} />Corte de caja</h3>
            <p className="page-subtitle">Incluye todas las ventas desde el último corte de la tienda</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        {pendiente && (
          pendiente.ventas.length === 0 && pendiente.devoluciones.length === 0 && pendiente.caja.length === 0 ? (
            <div className="alert alert-info"><i className="fas fa-check-circle mr-2" />No hay ventas, devoluciones ni movimientos de caja pendientes de corte en esta tienda.</div>
          ) : (
            <div className="row">
              <div className="col-lg-7 mb-3">
                <div className="form-section">
                  <div className="form-section-header"><i className="fas fa-calculator" /><h4 className="form-section-title">Cuadre por forma de pago</h4></div>
                  <div className="form-section-body">
                    <table className="table table-sm mb-0">
                      <thead><tr><th>Forma de pago</th><th className="text-right">Sistema</th><th style={{ minWidth: 120 }}>Entrega</th><th className="text-right">Diferencia</th></tr></thead>
                      <tbody>
                        {METODOS_K.map((k) => (
                          <tr key={k}>
                            <td style={{ verticalAlign: "middle" }}><strong>{METODOS[k]}</strong></td>
                            <td className="text-right" style={{ verticalAlign: "middle" }}>{dinero(pendiente.sistema[k])}</td>
                            <td><Input type="number" step="0.01" value={entregado[k]} placeholder={k === "efectivo" ? "Contado" : ""} onChange={(e) => setEntregado({ ...entregado, [k]: e.target.value })} /></td>
                            <td className="text-right" style={{ verticalAlign: "middle", fontWeight: 700, color: dif(k) === 0 ? "#059669" : "#dc2626" }}>{entregado[k] === "" ? "" : dinero(dif(k))}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 800 }}>
                          <td>TOTAL</td>
                          <td className="text-right">{dinero(pendiente.sistema.total)}</td>
                          <td className="text-right">{dinero(total)}</td>
                          <td className="text-right" style={{ color: r2(total - pendiente.sistema.total) === 0 ? "#059669" : "#dc2626" }}>{dinero(total - pendiente.sistema.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                    {pendiente.numMovCaja > 0 && (
                      <div className="mt-3 p-2" style={{ background: "#f8fafc", borderRadius: 8, fontSize: "0.85rem" }}>
                        <strong>Efectivo esperado:</strong> ventas {dinero(pendiente.ventasEfectivo)}
                        {pendiente.reembolsadoEfectivo > 0 && <> − devoluciones {dinero(pendiente.reembolsadoEfectivo)}</>}
                        {pendiente.fondos > 0 && <> + fondo {dinero(pendiente.fondos)}</>}
                        {pendiente.gastos > 0 && <> − gastos {dinero(pendiente.gastos)}</>}
                        {pendiente.retiros > 0 && <> − retiros {dinero(pendiente.retiros)}</>}
                        {" "}= <strong>{dinero(pendiente.sistema.efectivo)}</strong>
                        <div className="mt-1">
                          {pendiente.caja.filter((m) => m.estatus === "Activo").map((m) => (
                            <div key={m._id} className="text-muted">{m.tipo} {m.folio} · {m.concepto}{m.recibio ? ` (recibió ${m.recibio})` : ""}: {m.tipo === "Fondo" ? "+" : "−"}{dinero(m.importe)}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: 12 }}>
                      <label className="mb-0"><strong>Se queda de fondo en caja</strong></label>
                      <Input type="number" step="0.01" min="0" style={{ width: 140 }} placeholder="0" value={fondoQueda} onChange={(e) => setFondoQueda(e.target.value)} />
                      {entregado.efectivo !== "" && (
                        <span style={{ fontSize: "1.05rem" }}>Efectivo a entregar: <strong style={{ color: aEntregar < 0 ? "#dc2626" : "#0f172a" }}>{dinero(aEntregar)}</strong></span>
                      )}
                    </div>
                    <small className="text-muted">El fondo se cuenta junto con el efectivo; se queda en el cajón y abre el siguiente corte.</small>
                    <Input type="textarea" rows={2} className="mt-3" placeholder="Observaciones (obligatorias si hay diferencia)" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                    <div className="submit-bar">
                      <Button color="success" size="lg" disabled={guardando} onClick={hacerCorte}>
                        {guardando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-lock mr-2" />Cerrar corte e imprimir</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-5 mb-3">
                <div className="stats-row">
                  <div className="stat-chip"><i className="fas fa-receipt" style={{ color: "#10b981" }} />Ventas <span>{pendiente.numVentas}</span></div>
                  <div className="stat-chip"><i className="fas fa-ban" style={{ color: "#ef4444" }} />Canceladas <span>{pendiente.numCanceladas}</span></div>
                  <div className="stat-chip"><i className="fas fa-tshirt" style={{ color: "#3b82f6" }} />Piezas <span>{pendiente.piezas}</span></div>
                  <div className="stat-chip"><i className="fas fa-tags" style={{ color: "#f59e0b" }} />Descuentos <span>{dinero(pendiente.descuentos)}</span></div>
                  {pendiente.numDevoluciones > 0 && (
                    <div className="stat-chip"><i className="fas fa-undo" style={{ color: "#ef4444" }} />Regresado <span>{dinero(pendiente.reembolsado)}</span></div>
                  )}
                </div>
                {pendiente.devoluciones.length > 0 && (
                  <div className="table-responsive mb-2">
                    <table className="table table-sm">
                      <thead><tr><th>Devolución</th><th>Ticket</th><th className="text-right">Saldo a favor</th><th>Se regresó</th></tr></thead>
                      <tbody>
                        {pendiente.devoluciones.map((d) => (
                          <tr key={d._id}>
                            <td>{d.tipo} {d.folio}</td><td>{d.ventaFolio}</td><td className="text-right">{dinero(d.credito)}</td>
                            <td>{d.reembolso && d.reembolso.importe > 0 ? `${dinero(d.reembolso.importe)} ${METODOS[d.reembolso.metodo]}` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="table-responsive" style={{ maxHeight: 320, overflowY: "auto" }}>
                  <table className="table table-sm table-dark-header">
                    <thead><tr><th>Folio</th><th>Hora</th><th className="text-right">Total</th><th>Pago</th></tr></thead>
                    <tbody>
                      {pendiente.ventas.map((v) => (
                        <tr key={v._id} style={v.estatus === "Cancelada" ? { textDecoration: "line-through", color: "#94a3b8" } : {}}>
                          <td>{v.folio}</td>
                          <td>{hora(v.fecha)}</td>
                          <td className="text-right">{dinero(v.total)}</td>
                          <td>{v.pagos.map((p) => METODOS[p.metodo]).join(" + ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        )}

        <div className="form-section mt-2">
          <div className="form-section-header dark"><i className="fas fa-history" /><h4 className="form-section-title">Cortes anteriores</h4></div>
          <div className="form-section-body">
            <div className="filter-bar">
              <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead><tr><th>Folio</th><th>Fecha</th><th>Hizo</th><th className="text-right">Ventas</th><th className="text-right">Sistema</th><th className="text-right">Entregado</th><th className="text-right">Diferencia</th><th /></tr></thead>
                <tbody>
                  {historial.length === 0 && <tr><td colSpan={8} className="text-center text-muted">Sin cortes en el periodo</td></tr>}
                  {historial.map((c) => (
                    <tr key={c._id}>
                      <td>{c.folio}</td>
                      <td>{fechaHora(c.fecha)}</td>
                      <td>{c.user ? `${c.user.nombre} ${c.user.apellido}` : ""}</td>
                      <td className="text-right">{c.numVentas}</td>
                      <td className="text-right">{dinero(c.sistema.total)}</td>
                      <td className="text-right">{dinero(c.entregado.total)}</td>
                      <td className="text-right" style={{ fontWeight: 700, color: c.diferencia.total === 0 ? "#059669" : "#dc2626" }}>{dinero(c.diferencia.total)}</td>
                      <td><Button size="sm" color="danger" onClick={() => reimprimir(c._id)} title="Imprimir"><i className="fas fa-print" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PosCorte;
